/*
 * Accessibility enhancement: keeps keyboard focus contained inside the
 * nav and cart drawers while they're visually open, and returns focus
 * to whatever was focused before, once they close.
 *
 * This does NOT hook into any click handlers or class names the theme
 * uses to open/close the drawers (that logic lives in compiled theme.js
 * and isn't inspectable here). Instead it watches whether #NavDrawer and
 * #CartDrawer are actually on-screen, frame by frame, and reacts purely
 * to that — so it works no matter how the theme opens/closes them.
 */
(function () {
  'use strict';

  var FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
    'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var activeHandler = null;
  var lastFocusedElement = null;
  var currentTrappedDrawer = null;

  function isOnScreen(el) {
    var rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      return false;
    }
    return (
      rect.right > 0 &&
      rect.left < (window.innerWidth || document.documentElement.clientWidth) &&
      rect.bottom > 0 &&
      rect.top < (window.innerHeight || document.documentElement.clientHeight)
    );
  }

  function getFocusableElements(container) {
    var all = container.querySelectorAll(FOCUSABLE_SELECTOR);
    var visible = [];
    for (var i = 0; i < all.length; i++) {
      if (all[i].offsetParent !== null) {
        visible.push(all[i]);
      }
    }
    return visible;
  }

  function trapFocus(container) {
    releaseFocusTrap();
    lastFocusedElement = document.activeElement;

    var focusable = getFocusableElements(container);
    if (focusable.length) {
      focusable[0].focus();
    }

    function handleKeydown(e) {
      if (e.key !== 'Tab') {
        return;
      }
      var els = getFocusableElements(container);
      if (!els.length) {
        return;
      }
      var first = els[0];
      var last = els[els.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first || !container.contains(document.activeElement)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last || !container.contains(document.activeElement)) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeydown, true);
    activeHandler = handleKeydown;
    currentTrappedDrawer = container;
  }

  function releaseFocusTrap() {
    if (activeHandler) {
      document.removeEventListener('keydown', activeHandler, true);
      activeHandler = null;
    }
    currentTrappedDrawer = null;
  }

  function restoreFocus() {
    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
      lastFocusedElement.focus();
    }
    lastFocusedElement = null;
  }

  function init() {
    var candidates = [document.getElementById('NavDrawer'), document.getElementById('CartDrawer')];
    var drawers = [];
    for (var i = 0; i < candidates.length; i++) {
      if (candidates[i]) {
        drawers.push({ el: candidates[i], wasOpen: false });
      }
    }
    if (!drawers.length) {
      return;
    }

    function poll() {
      for (var i = 0; i < drawers.length; i++) {
        var d = drawers[i];
        var open = isOnScreen(d.el);
        if (open && !d.wasOpen) {
          trapFocus(d.el);
        } else if (!open && d.wasOpen && currentTrappedDrawer === d.el) {
          releaseFocusTrap();
          restoreFocus();
        }
        d.wasOpen = open;
      }
      window.requestAnimationFrame(poll);
    }
    window.requestAnimationFrame(poll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
